const VERTEX_SHADER = `
  attribute vec2 aPosition;
  attribute vec2 aUv;
  varying vec2 vUv;
  void main() {
    vUv = aUv;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER = `
  precision mediump float;
  uniform sampler2D uTexture;
  uniform vec2 uMouse;
  uniform float uStrength;
  uniform float uRadius;
  uniform float uTime;
  uniform float uTimeSinceMove;
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  varying vec2 vUv;
  void main() {
    float d = distance(vUv, uMouse);
    vec2 delta = vUv - uMouse;
    float len = length(delta);
    vec2 uvCover = vUv * uUvScale + uUvOffset;
    vec2 uv = vec2(uvCover.x, 1.0 - uvCover.y);
    vec2 disp = vec2(0.0, 0.0);
    if (len > 0.001) {
      float influence = smoothstep(uRadius, 0.0, d);
      float decay = exp(-d * 1.15) * influence;
      float damp = uTimeSinceMove < 2.0 ? 1.0 : exp(-(uTimeSinceMove - 2.0) * 0.55);
      float rampDown = smoothstep(2.0, 2.5, uTimeSinceMove);
      float active = 1.0 - rampDown;
      float phase = d * 30.0 - uTime * 3.8;
      float wave = sin(phase);
      float amp = decay * damp * active * wave * uStrength;
      vec2 dir = delta / len;
      disp = dir * amp;
    }
    float surfaceWobble = 0.0004 * sin(vUv.x * 38.0) * sin(vUv.y * 38.0);
    disp += surfaceWobble * vec2(1.0, 0.7);
    vec2 screenUV = vUv + disp;
    uvCover = screenUV * uUvScale + uUvOffset;
    uv = vec2(uvCover.x, 1.0 - uvCover.y);
    gl_FragColor = texture2D(uTexture, clamp(uv, 0.0, 1.0));
  }
`;

const LERP_SPEED = 5;
const STRENGTH = 0.019;
const RADIUS = 1.5;
const DEFAULT_IMAGE_URL = '/hero_bg.jpg';

export class HeroLiquidBackground {
  private section: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private gl: WebGLRenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private texture: WebGLTexture | null = null;
  private positionBuffer: WebGLBuffer | null = null;
  private uvBuffer: WebGLBuffer | null = null;
  private targetMouse = { x: 0.5, y: 0.5 };
  private currentMouse = { x: 0.5, y: 0.5 };
  private rafId: number | null = null;
  private boundOnMouseMove: (e: MouseEvent) => void;
  private boundOnResize: () => void;
  private boundOnContextLost: (e: Event) => void;
  private boundOnContextRestored: () => void;
  private reduceMotion = false;
  private imgWidth = 0;
  private imgHeight = 0;
  private startTime = 0;
  private lastMouseTime = 0;
  private lastFrameTime = 0;
  /** Current image URL so we can re-init after WebGL context restore. */
  private currentImageUrl = DEFAULT_IMAGE_URL;
  private intersectionObserver: IntersectionObserver | null = null;

  constructor() {
    this.boundOnMouseMove = this.onMouseMove.bind(this);
    this.boundOnResize = this.onResize.bind(this);
    this.boundOnContextLost = this.onContextLost.bind(this);
    this.boundOnContextRestored = this.onContextRestored.bind(this);

    this.section = document.querySelector('.scroll-hero-section');
    this.canvas = document.getElementById('hero-liquid-canvas') as HTMLCanvasElement | null;

    this.reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.canvas || !this.section || this.reduceMotion) {
      if (this.canvas) this.canvas.classList.remove('hero-liquid-canvas-ready');
      return;
    }

    this.loadImageAndInit();
  }

  private loadImageAndInit(url?: string): void {
    const src = url ?? DEFAULT_IMAGE_URL;
    this.currentImageUrl = src;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this.initWebGL(img);
    };
    img.onerror = () => {
      if (this.canvas) this.canvas.classList.remove('hero-liquid-canvas-ready');
    };
    img.src = src;
  }

  /** Update the canvas texture to a new image (e.g. after hero background rotation). */
  public setImage(url: string): void {
    this.currentImageUrl = url;
    if (!this.gl || !this.texture || !this.canvas) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (!this.gl || !this.texture) return;
      this.imgWidth = img.naturalWidth || img.width || 1;
      this.imgHeight = img.naturalHeight || img.height || 1;
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, img);
      this.onResize();
    };
    img.onerror = () => {};
    img.src = url;
  }

  private initWebGL(img: HTMLImageElement): void {
    if (!this.canvas) return;

    const gl = (this.canvas.getContext('webgl') ||
      this.canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) {
      this.canvas.classList.remove('hero-liquid-canvas-ready');
      return;
    }
    this.gl = gl;
    this.imgWidth = img.naturalWidth || img.width || 1;
    this.imgHeight = img.naturalHeight || img.height || 1;

    this.canvas.addEventListener('webglcontextlost', this.boundOnContextLost);
    this.canvas.addEventListener('webglcontextrestored', this.boundOnContextRestored);

    const vs = this.compileShader(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = this.compileShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vs || !fs) {
      this.canvas.classList.remove('hero-liquid-canvas-ready');
      return;
    }

    const program = gl.createProgram();
    if (!program) {
      this.canvas.style.opacity = '0';
      return;
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      this.canvas.classList.remove('hero-liquid-canvas-ready');
      return;
    }
    this.program = program;

    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);

    const positions = new Float32Array([
      -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
    ]);
    const uvs = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);

    this.positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    this.uvBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, uvs, gl.STATIC_DRAW);

    this.startTime = performance.now() / 1000;
    this.lastMouseTime = this.startTime - 100;
    this.section!.addEventListener('mousemove', this.boundOnMouseMove);
    window.addEventListener('resize', this.boundOnResize);
    this.onResize();
    this.canvas.classList.add('hero-liquid-canvas-ready');
    this.setupVisibilityObserver();
    this.tick();
  }

  private onContextLost(e: Event): void {
    e.preventDefault();
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.section?.removeEventListener('mousemove', this.boundOnMouseMove);
    window.removeEventListener('resize', this.boundOnResize);
    this.canvas?.removeEventListener('webglcontextlost', this.boundOnContextLost);
    this.canvas?.removeEventListener('webglcontextrestored', this.boundOnContextRestored);
    this.gl = null;
    this.program = null;
    this.texture = null;
    this.positionBuffer = null;
    this.uvBuffer = null;
  }

  private onContextRestored(): void {
    this.loadImageAndInit(this.currentImageUrl);
  }

  /** When hero comes back into view after scroll, force a draw so the ripple doesn’t stay frozen. */
  private setupVisibilityObserver(): void {
    if (!this.section || this.intersectionObserver) return;
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && this.gl) this.draw();
      },
      { threshold: 0.1, rootMargin: '0px' }
    );
    this.intersectionObserver.observe(this.section);
  }

  private compileShader(
    type: number,
    source: string
  ): WebGLShader | null {
    if (!this.gl) return null;
    const shader = this.gl.createShader(type);
    if (!shader) return null;
    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      this.gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.section) return;
    const rect = this.section.getBoundingClientRect();
    this.targetMouse.x = (e.clientX - rect.left) / rect.width;
    this.targetMouse.y = 1.0 - (e.clientY - rect.top) / rect.height;
    this.lastMouseTime = performance.now() / 1000;
  }

  private onResize(): void {
    if (!this.canvas || !this.section || !this.gl) return;
    const rect = this.section.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.floor(rect.width * dpr);
    const h = Math.floor(rect.height * dpr);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.gl.viewport(0, 0, w, h);
    }
    this.draw();
  }

  private draw(): void {
    if (!this.gl || !this.program || !this.texture || !this.canvas) return;

    const now = performance.now() / 1000;
    const dt = this.lastFrameTime > 0 ? now - this.lastFrameTime : 1 / 60;
    this.lastFrameTime = now;
    const lerpFactor = 1.0 - Math.exp(-LERP_SPEED * dt);
    this.currentMouse.x += (this.targetMouse.x - this.currentMouse.x) * lerpFactor;
    this.currentMouse.y += (this.targetMouse.y - this.currentMouse.y) * lerpFactor;
    const timeSinceMove = Math.max(0, now - this.lastMouseTime);

    const gl = this.gl;
    const vpW = this.canvas.width;
    const vpH = this.canvas.height;
    const vpAspect = vpW / vpH;
    const imgAspect = this.imgWidth / this.imgHeight;
    let uvScaleX: number;
    let uvScaleY: number;
    let uvOffsetX: number;
    let uvOffsetY: number;
    if (vpAspect >= imgAspect) {
      uvScaleX = 1;
      uvScaleY = imgAspect / vpAspect;
      uvOffsetX = 0;
      uvOffsetY = 0.5 * (1 - uvScaleY);
    } else {
      uvScaleX = vpAspect / imgAspect;
      uvScaleY = 1;
      uvOffsetX = 0.5 * (1 - uvScaleX);
      uvOffsetY = 0;
    }

    gl.useProgram(this.program);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.uniform1i(gl.getUniformLocation(this.program, 'uTexture'), 0);
    gl.uniform2f(
      gl.getUniformLocation(this.program, 'uMouse'),
      this.currentMouse.x,
      this.currentMouse.y
    );
    gl.uniform1f(gl.getUniformLocation(this.program, 'uStrength'), STRENGTH);
    gl.uniform1f(gl.getUniformLocation(this.program, 'uRadius'), RADIUS);
    gl.uniform1f(gl.getUniformLocation(this.program, 'uTime'), now - this.startTime);
    gl.uniform1f(gl.getUniformLocation(this.program, 'uTimeSinceMove'), timeSinceMove);
    gl.uniform2f(gl.getUniformLocation(this.program, 'uUvScale'), uvScaleX, uvScaleY);
    gl.uniform2f(gl.getUniformLocation(this.program, 'uUvOffset'), uvOffsetX, uvOffsetY);

    const posLoc = gl.getAttribLocation(this.program, 'aPosition');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uvLoc = gl.getAttribLocation(this.program, 'aUv');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
    gl.enableVertexAttribArray(uvLoc);
    gl.vertexAttribPointer(uvLoc, 2, gl.FLOAT, false, 0, 0);

    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private tick = (): void => {
    this.draw();
    this.rafId = requestAnimationFrame(this.tick);
  };

  public dispose(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
    this.section?.removeEventListener('mousemove', this.boundOnMouseMove);
    window.removeEventListener('resize', this.boundOnResize);
    this.canvas?.removeEventListener('webglcontextlost', this.boundOnContextLost);
    this.canvas?.removeEventListener('webglcontextrestored', this.boundOnContextRestored);
    if (this.gl && this.program) {
      this.gl.deleteProgram(this.program);
      if (this.texture) this.gl.deleteTexture(this.texture);
      if (this.positionBuffer) this.gl.deleteBuffer(this.positionBuffer);
      if (this.uvBuffer) this.gl.deleteBuffer(this.uvBuffer);
    }
    this.gl = null;
    this.program = null;
    this.texture = null;
    this.positionBuffer = null;
    this.uvBuffer = null;
    this.section = null;
    this.canvas = null;
  }
}
